'use client';

import { getCoupon, getCouponId } from '@/apis/land/land.api';
import UKToast from '@/components/UKToast';
import { useTranslation } from '@/i18n/client';
import clientApp from '@/utils/client.app';
import { uaParse } from '@/intl-jdi-common-ssr/utils/uaParser';
import Button from '@mui/material/Button';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { ECouponGetState, ECouponState, ECouponType, ICouponInfo } from '../type';
import { calcSymbol } from '@/utils/price';

const CouponContent = ({
  couponInfo,
  setCouponInfo,
  localInfo,
}: {
  couponInfo: ICouponInfo;
  setCouponInfo: (couponInfo: ICouponInfo) => void;
  localInfo: any;
}) => {
  const { t } = useTranslation();
  const locale = localInfo?.languageCode;
  const currency = localInfo?.currencyCode;
  const MButtonText = {
    Collect: `${t('coupon.btn.collect')}`,
    GoShopping: `${t('coupon.btn.goShopping')}`,
    Expired: `${t('coupon.btn.expired')}`,
    AllClaimed: `${t('coupon.btn.allClaimed')}`,
    NotStart: `${t('coupon.btn.notStart')}`,
  };
  const handleCouponStateUpdate = (code: string, msg: string) => {
    switch (code) {
      case '400018':
        UKToast.show({
          content: msg ?? `${t('coupon.claimed')}`,
          icon: 'error',
        });
        setCouponInfo({ ...couponInfo, couponState: ECouponState.AllClaimed });
        break;
      case '400024':
        UKToast.show({
          content: msg ?? `${t('coupon.expired')}`,
          icon: 'error',
        });
        setCouponInfo({ ...couponInfo, couponState: ECouponState.Expired });
        break;
      default:
        if (code !== '401') {
          UKToast.show({
            content: msg ?? `${t('coupon.error')}`,
            icon: 'error',
          });
        }
    }
  };
  const router = useRouter();

  const [isCollecting, setIsCollecting] = useState(false);
  async function collectCoupon(couponInfo: ICouponInfo) {
    const { activityId, roleId, encryptedKey } = couponInfo;
    if (isCollecting) return;
    try {
      setIsCollecting(true);
      const res = await getCoupon({ activityId, roleId, encryptedKey });

      if (res.code === 200 || res.code === '200') {
        if (res.data.result && res.data.takeStatus === 1) {
          UKToast.show({
            content: res?.msg || `${t('coupon.success')}`,
            icon: 'success',
          });
          setCouponInfo({ ...couponInfo, couponGetState: ECouponGetState.PendingUse });
        }
      } else {
        handleCouponStateUpdate(res.code, res.msg);
      }
    } catch {
      UKToast.show({
        content: `${t('coupon.error')}`,
        icon: 'error',
      });
    } finally {
      setIsCollecting(false);
    }
  }

  const [isQueryingCouponId, setIsQueryingCouponId] = useState(false);
  async function goShopping({ batchId }: ICouponInfo) {
    if (isQueryingCouponId) return;
    try {
      const res = await getCouponId({ batchId });
      if (res.code === 200 || res.code === '200') {
        const { couponId } = res.data;
        // 如果是app则跳转原生凑单页，如果是h5则跳转h5凑单页
        if (uaParse().isInJoybuyApp) {
          clientApp.goSearchCoupon({ batchId: batchId, couponId: couponId });
        } else {
          router.push(`/s-for-addon?&type=1&couponId=${couponId}&batchId=${batchId}`);
        }
      } else {
        UKToast.show({
          content: res.msg ?? `${t('coupon.error')}`,
          icon: 'error',
        });
      }
    } catch (e) {
      UKToast.show({
        content: `${t('coupon.error')}`,
        icon: 'error',
      });
    } finally {
      setIsQueryingCouponId(false);
    }
  }

  return (
    <div className='CouponContent'>
      <div className='left'>
        <div className='off'>
          <span className='offDiscount'>
            <span className='discount'>
              {calcSymbol(couponInfo.discount?.toString() || '', locale, currency)}
            </span>
            {couponInfo.couponType === ECouponType.Off && (
              <span className='subscript'>{t('coupon.onlyOff')}</span>
            )}
            {couponInfo.couponType === ECouponType.ShippingOff && (
              <span className='subscript'>{t('coupon.shippingOff')}</span>
            )}
          </span>
          <span className='quotaLimit'>
            {couponInfo.quota
              ? `${t('coupon.over', { num: calcSymbol(couponInfo.quota.toString() || '', locale, currency) })} `
              : `${t('coupon.noLimit')}`}
          </span>
        </div>
        {couponInfo.couponUseLimit && couponInfo.couponType !== ECouponType.ShippingOff && (
          <div className='couponUseLimit'>{couponInfo.couponUseLimit}</div>
        )}
      </div>
      <div className='right'>
        {/* 未开始 */}
        {couponInfo.couponState === ECouponState.NotStarted && (
          <Button
            sx={{ '&.Mui-disabled': { background: '#FF9FA7', color: '#F5F6FA' } }}
            disabled
            variant='contained'
          >
            {MButtonText.NotStart}
          </Button>
        )}
        {/* 进行中  未领取*/}
        {couponInfo.couponState === ECouponState.InProgress &&
          couponInfo.couponGetState === ECouponGetState.NotReceived && (
            <Button
              variant='contained'
              sx={{ background: '#CC0C1C' }}
              onClick={() => collectCoupon(couponInfo)}
            >
              {MButtonText.Collect}
            </Button>
          )}
        {/* 进行中  待使用 且不为运费券 */}
        {couponInfo.couponState === ECouponState.InProgress &&
          couponInfo.couponGetState === ECouponGetState.PendingUse &&
          couponInfo.couponType !== ECouponType.ShippingOff && (
            <Button
              sx={{ background: '#fff', border: '#CC0C1C .5px solid', color: '#CC0C1C' }}
              onClick={() => goShopping(couponInfo)}
            >
              {MButtonText.GoShopping}
            </Button>
          )}
        {/* 进行中  已使用&已过期*/}
        {couponInfo.couponState === ECouponState.InProgress &&
          couponInfo.couponGetState === ECouponGetState.UsedOrExpired && (
            <Button variant='contained' sx={{ background: '#CC0C1C' }}>
              {MButtonText.Expired}
            </Button>
          )}
        {/* 已过期 已抢光 */}
        {[ECouponState.Expired].includes(couponInfo.couponState) && (
          <Button
            sx={{ '&.Mui-disabled': { background: '#C2C4CC', color: '#F5F6FA' } }}
            disabled
            variant='contained'
          >
            {MButtonText.Expired}
          </Button>
        )}
        {[ECouponState.AllClaimed].includes(couponInfo.couponState) && (
          <Button
            sx={{ '&.Mui-disabled': { background: '#C2C4CC', color: '#F5F6FA' } }}
            disabled
            variant='contained'
          >
            {MButtonText.AllClaimed}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CouponContent;
